// Global
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Checkbox, Input } from '@/ui';
import { CategoryIsHeavyField } from '../CategoryIsHeavyField';
import { FormItem, IFormInstance } from '@/components';
import {
  StyledFeedLink,
  StyledForm,
  StyledFormWrapper,
  StyledLinkBlock,
  StyledRestorationCheckboxWrap,
} from '../Styled';
import CategoryNameSwitch from '../../components/CategoryNameSwitch/CategoryNameSwitch';
import EditCategoryPhotoField from '../EditCategoryPhotoField/EditCategoryPhotoField';
import ParentCategorySelect from '../ParentCategorySelect/ParentCategorySelect';

// Models
import { ICategory, ICategoryDetails } from '@/models/categories.model';

// Helpers
import { EditCategoryFieldNames } from '../../helpers/types';
import { useEditCategoryValidationRules } from '../../hooks';

type TEditCategoryForm = {
  formInstance: IFormInstance;
  onSubmit: (values: ICategoryDetails) => void;
  categoryDetails: ICategoryDetails;
  categories: ICategory[] | null;
};

const EditCategoryForm = ({ categoryDetails, formInstance, onSubmit, categories }: TEditCategoryForm) => {
  const { t } = useTranslation(['categories']);

  const rules = useEditCategoryValidationRules();

  useEffect(() => {
    formInstance.setFieldsValue(categoryDetails);
  }, [categoryDetails, formInstance]);

  return (
    <StyledFormWrapper>
      <StyledForm onFinish={onSubmit} form={formInstance} key={categoryDetails.id} initialValues={categoryDetails}>
        <CategoryNameSwitch
          label={t('category_name')}
          formItemName={EditCategoryFieldNames.LOCALE}
          locale={categoryDetails.locale}
          validationRules={rules[EditCategoryFieldNames.LOCALE]}
        />

        <FormItem
          name={EditCategoryFieldNames.SLUG}
          label={t('category_alias')}
          rules={rules[EditCategoryFieldNames.SLUG]}
        >
          <Input />
        </FormItem>

        <FormItem
          name={EditCategoryFieldNames.GOOGLE_FEED_ID}
          label={t('category_google_feed_id')}
          rules={rules[EditCategoryFieldNames.GOOGLE_FEED_ID]}
        >
          <Input />
        </FormItem>
        <StyledLinkBlock>
          <span>{t('category_google_feed_id_text')}</span>&nbsp;
          <StyledFeedLink to={'https://support.google.com/merchants/answer/6324436?hl=uk'} target="_blank">
            {t('category_redirect')}
          </StyledFeedLink>
        </StyledLinkBlock>

        <CategoryIsHeavyField fieldName={EditCategoryFieldNames.IS_HEAVY} />

        {categoryDetails.parent_id ? (
          <StyledRestorationCheckboxWrap>
            <FormItem name={EditCategoryFieldNames.IS_RESTORATION} valuePropName="checked">
              <Checkbox>{t('category_is_restoration')}</Checkbox>
            </FormItem>
          </StyledRestorationCheckboxWrap>
        ) : null}

        <EditCategoryPhotoField categoryDetails={categoryDetails} />

        <ParentCategorySelect categories={categories} formItemName={EditCategoryFieldNames.PARENT_ID} />
      </StyledForm>
    </StyledFormWrapper>
  );
};

export default EditCategoryForm;
